import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Common, { FontAwesome } from 'src/components/Common';
import useTheme from 'src/hooks/useTheme';
import styles, { getParticipantStyles, getParticipantListStyles, headerStyles } from './style';
import useTranslation from 'src/hooks/useTranslation';
import useChatState from 'src/screens/Chat/hooks/useChatState';
import { isThreadServiceEnabled } from 'src/graphql/thread-service.helpers';
import { Participant, ParticipantUnassigned } from 'src/components/MentionsComponents/Participants';
import { FEATURE_FLAGS, isFeatureFlagEnabled } from 'src/config/featureFlags';
import { getTicketParticipants } from 'src/utils/mention.utils';

const ChatHeader = ({ ticket: ticketProps }) => {
  const { theme, isDarkMode } = useTheme();
  const { translate } = useTranslation();
  const { chatState } = useChatState();
  const unassignedText = translate('header.assignee.unassigned');
  const ticket = chatState.activeTicket ?? ticketProps;
  let ticketParticipants = getTicketParticipants(ticket);
  const participantsCount = ticketParticipants.length;
  const shouldShowParticipantsGroup = participantsCount > 1;
  // in UI, we show only first 3 participants
  ticketParticipants = ticketParticipants.slice(0, 3);

  const getHeaderProgressInfo = () => {
    if (shouldShowParticipantsGroup) {
      return `#${ticket.systemId} - ${participantsCount} People - ${ticket.status}`;
    }
    return `#${ticket.systemId} - ${ticket.status}`;
  };

  const renderTicketParticipants = () => {
    if (shouldShowParticipantsGroup) {
      return ticketParticipants.map((participant, index) => (
        <Participant
          key={participant.id}
          size={37}
          participantName={participant.name}
          style={getParticipantStyles({ offset: index, zIndex: participantsCount - index })}
        />
      ));
    }

    if (ticket.assigneeId) {
      return (
        <Participant
          size={37}
          participantName={ticket.assignee}
          style={getParticipantStyles({ offset: 0, zIndex: 1 })}
        />
      );
    }

    return <ParticipantUnassigned size={40} />;
  };

  if (isThreadServiceEnabled() && isFeatureFlagEnabled(FEATURE_FLAGS.THREAD_REDESIGN)) {
    return (
      <Common.View transparent style={headerStyles.wrapper}>
        <Common.View transparent style={getParticipantListStyles({ participantsCount })}>
          {renderTicketParticipants()}
        </Common.View>

        <Common.View transparent style={headerStyles.details}>
          <Common.Text numberOfLines={1} color={theme.primaryColor} style={headerStyles.summary}>
            {ticket.summary}
          </Common.Text>
          <Common.Text size={12} numberOfLines={1}>
            {getHeaderProgressInfo()}
          </Common.Text>
        </Common.View>
      </Common.View>
    );
  }

  return (
    <Common.Row alignItems="center">
      {ticket.assignee?.image ? (
        <Common.Avatar testID="avatar" source={{ uri: ticket.assignee.image }} />
      ) : (
        <Common.View
          testID="default-avatar"
          style={[
            styles.avatar,
            { backgroundColor: theme.primaryColor },
            isDarkMode && { backgroundColor: theme.background },
          ]}
        >
          <FontAwesome name="user-o" style={{ color: '#fff' }} size={25} />
        </Common.View>
      )}
      <Common.View style={styles.info}>
        <Common.Text bold color={theme.primaryColor} numberOfLines={1}>
          {ticket?.summary}
        </Common.Text>
        <Common.Text size={13} color={theme.primaryColor}>
          <Common.Text size={13} color={theme.primaryColor}>{`${ticket?.assignee || unassignedText} · `}</Common.Text>
          {`#${ticket?.number || ticket?.systemId}`}
        </Common.Text>
      </Common.View>
    </Common.Row>
  );
};

ChatHeader.propTypes = {
  ticket: PropTypes.object.isRequired,
};

export default memo(ChatHeader);
